import React from 'react';
import { Paper, Typography, Button, Box, Stack, useTheme } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useAlert } from '../../context/AlertContext';
import { alpha } from '@mui/material/styles';

const AlertWidget = () => {
  const { alert } = useAlert();
  const theme = useTheme();

  if (!alert) return null;

  const Icon = alert.icon || NotificationsActiveIcon;
  
  // Determine colors based on severity
  const colorMap = {
    warning: 'warning',
    error: 'error',
    success: 'success',
    info: 'primary'
  };
  const colorKey = colorMap[alert.severity] || 'primary';
  const mainColor = theme.palette[colorKey].main;

  // Handle actions
  const actions = alert.actions || (alert.action ? [alert.action] : []);

  return (
    <Paper
      variant="outlined"
      sx={{
        py: 0.75,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        minWidth: 'auto',
        maxWidth: { xs: 'calc(100vw - 48px)', sm: 'fit-content' }, 
        borderRadius: 3,
        bgcolor: alpha(mainColor, 0.12),
        border: `1px solid ${alpha(mainColor, 0.3)}`,
        boxShadow: `0 4px 12px ${alpha(mainColor, 0.15)}`,
        backdropFilter: 'blur(8px)',
        animation: 'slideDown 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        '@keyframes slideDown': {
          from: { opacity: 0, transform: 'translateY(-10px) scale(0.95)' },
          to: { opacity: 1, transform: 'translateY(0) scale(1)' }
        },
        overflow: 'hidden'
      }}
    >
      {/* Flex Container for Icon + Text that shrinks */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1.5, 
        flexGrow: 1, 
        minWidth: 0,
        mr: actions.length > 0 ? 2 : 0
      }}>
        <Icon sx={{ fontSize: 20, color: mainColor, flexShrink: 0 }} />
        
        {/* Marquee Text Container */}
        <Box sx={{ 
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          position: 'relative',
          flex: 1,
          minWidth: 0,
        }}>
          {/* Inner wrapper that contains both text copies and animates */}
          <Box
            sx={{
              display: { xs: 'inline-flex', sm: 'block' },
              animation: { 
                xs: 'marquee 12s linear infinite',
                sm: 'none' 
              },
              '&:hover': {
                animationPlayState: 'paused'
              },
              '@keyframes marquee': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(-50%)' }
              },
            }}
          >
            {/* First copy */}
            <Typography 
              component="span"
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '0.85rem', 
                color: theme.palette.text.primary,
                letterSpacing: '0.02em',
                paddingRight: { xs: 6, sm: 0 },
              }}
            >
              {alert.message}
            </Typography>
            {/* Second copy for seamless loop (mobile only) */}
            <Typography 
              component="span"
              variant="body2" 
              aria-hidden="true"
              sx={{ 
                display: { xs: 'inline', sm: 'none' },
                fontWeight: 600, 
                fontSize: '0.85rem', 
                color: theme.palette.text.primary,
                letterSpacing: '0.02em',
                paddingRight: 6,
              }}
            >
              {alert.message}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Actions (Fixed width / no shrink) */}
      {actions.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {actions.map((action, index) => (
              <Button 
                  key={index}
                  size="small" 
                  variant={action.variant || "contained"}
                  color={action.color || colorKey}
                  onClick={action.onClick}
                  sx={{ 
                      minWidth: 'auto', 
                      py: 0.25, 
                      px: 1.5, 
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: 'none',
                      boxShadow: action.variant === 'contained' ? 2 : 0,
                      whiteSpace: 'nowrap'
                  }}
              >
              {action.label}
              </Button>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default AlertWidget;